import * as React from "react";
import { Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import * as styles from "../styles/footer.module.scss";
import { maxWidth } from "../constants";
import { useSiteMetadata } from "../hooks/useSiteMetadata";

import LinkedInSvg from "../assets/icons/linkedin.svg";
import FacebookSvg from "../assets/icons/facebook.svg";
import InstagramSvg from "../assets/icons/instagram.svg";
import TikTokSvg from "../assets/icons/tiktok.svg";
import ThemeToggle from "./themeToggle";
import PoweredBy from "./poweredBy";

const Linkedin = ({ size = 24, ...props }) => (
  <LinkedInSvg width={size} height={size} {...props} />
);

const Facebook = ({ size = 24, ...props }) => (
  <FacebookSvg width={size} height={size} {...props} />
);

const Instagram = ({ size = 24, ...props }) => (
  <InstagramSvg width={size} height={size} {...props} />
);

const TikTok = ({ size = 24, ...props }) => (
  <TikTokSvg width={size} height={size} {...props} />
);

function SocialButton({ href, label, Icon }) {
  return (
    <a
      className={styles.socialButton}
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      title={label}
      onClick={() => {
        window.gtag?.("event", "social_click", {
          content_type: "social",
          content_id: label,
          section: "footer",
          page_location: window.location.pathname,
        });
      }}
    >
      <Icon
        className={styles.socialIcon}
        aria-hidden="true"
        focusable="false"
      />
    </a>
  );
}

function Footer() {
  const { siteName, appUrl } = useSiteMetadata();

  return (
    <footer className={styles.footer}>
      <div className={styles.inner} style={{ maxWidth }}>
        <div className={styles.rowLogo}>
          <div className={styles.logo}>
            <StaticImage
              src="../../src/assets/logos/remax-logo.png"
              alt={siteName}
              placeholder="none"
            />
          </div>
        </div>

        <div className={styles.rowContent}>
          <p className={styles.description}>
            {siteName} is a premier real estate brokerage under REMAX
            Philippines, delivering expert guidance in buying, selling, and
            leasing properties.
          </p>

          <nav className={styles.links} aria-label="Footer navigation">
            <Link
              to="/about-us"
              className={styles.link}
              activeClassName={styles.activeLink}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "about_us",
                  menu_type: "footer",
                  destination: "/about-us",
                  page_location: window.location.pathname,
                });
              }}
            >
              About Us
            </Link>
            <Link
              to="/agents"
              className={styles.link}
              activeClassName={styles.activeLink}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "agents",
                  menu_type: "footer",
                  destination: "/agents",
                  page_location: window.location.pathname,
                });
              }}
            >
              Agents
            </Link>
            <Link
              to="/#locations"
              className={styles.link}
              activeClassName={styles.activeLink}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "locations",
                  menu_type: "footer",
                  destination: "/#locations",
                  page_location: window.location.pathname,
                });
              }}
            >
              Locations
            </Link>
            <Link
              to="/faqs"
              className={styles.link}
              activeClassName={styles.activeLink}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "faqs",
                  menu_type: "footer",
                  destination: "/faqs",
                  page_location: window.location.pathname,
                });
              }}
            >
              FAQs
            </Link>
            <Link
              to="/contact-us"
              className={styles.link}
              activeClassName={styles.activeLink}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "contact_us",
                  menu_type: "footer",
                  destination: "/contact-us",
                  page_location: window.location.pathname,
                });
              }}
            >
              Contact Us
            </Link>
            <Link
              to={appUrl}
              target="_blank"
              rel="noreferrer"
              className={styles.link}
              onClick={() => {
                window.gtag?.("event", "select_content", {
                  content_type: "navigation",
                  content_id: "agent_login",
                  menu_type: "footer",
                  destination: appUrl,
                  page_location: window.location.pathname,
                });
              }}
            >
              Agent Login
            </Link>
          </nav>
        </div>

        <div className={styles.rowBottom}>
          <small className={styles.credits}>
            © {siteName} {new Date().getFullYear()}. All Rights Reserved.
            <br />
            <ThemeToggle variant="text" />
            <span className={styles.divider}>&middot;</span>
            <PoweredBy />
          </small>

          <div className={styles.social} aria-label="Social links">
            <SocialButton
              href="https://www.linkedin.com/company/re-max-capital-philippines"
              label="LinkedIn"
              Icon={Linkedin}
            />
            <SocialButton
              href="https://www.instagram.com/remaxcapital.ph/"
              label="Instagram"
              Icon={Instagram}
            />
            <SocialButton
              href="https://www.facebook.com/remaxcapitalalabang"
              label="Facebook"
              Icon={Facebook}
            />
            <SocialButton
              href="https://www.tiktok.com/@remaxcapitalph"
              label="TikTok"
              Icon={TikTok}
            />
          </div>
        </div>
      </div>
    </footer>
  );
}

export default React.memo(Footer);
